import BaseReportComponent from '@/report/components/BaseReportComponent';
import { computed, ComputedRef, defineComponent, onMounted, Ref, ref } from 'vue';
import CoverPageVue from '../components/CoverPage.vue';
import TeamCompareQuestion from '@/report/components/partials/TeamCompareQuestions.vue';
import TeamCompare from '../partials/TeamCompare.vue';
import TeamTable from '../partials/TeamTable.vue';
import TeamHeatmap from '../partials/TeamHeatmap.vue';
import { CategoryType } from '@/report/models/CategoryType';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import ProjectExportData from '@/report/models/IProjectExportData';
import { useRoute } from 'vue-router';
import ITeamReportResponse from '@/report/models/ITeamReportResponse';
import he from 'he';
import BarChartScore from '@/report/models/Charts/BarChartScore';
import ISection from '@/report/models/ISection';
import { ReportService } from '@/generic/services/reportService';

export default defineComponent({
    extends: BaseReportComponent,
    components: {
        CoverPage: CoverPageVue,
        TeamCompareQuestion,
        TeamCompare,
        TeamTable,
        TeamHeatmap,
    },
    props: {
        type: String,
        exposed: Boolean,
    },
    setup(props) {
        const loading = ref(true);
        const comparing = ref(true);
        const store = useStore();
        const { t } = useI18n();
        const compareTo: Ref<string> = ref('');
        const projectData: Ref<ProjectExportData> = ref();
        const { params, query } = useRoute();
        const projectId: number = +params.projectId;
        const token: string = query.token.toString();
        const thresholdPrefix: ComputedRef<string> = computed(() => (params.reportAlias === 'epsi_c' || params.reportAlias === 'epsi' ? 'ORG_' : ''));
        const epsiCReportData: Ref<ITeamReportResponse> = ref();
        const epsiAReportData: Ref<ITeamReportResponse> = ref();
        const epsiCSelected: Ref<boolean> = ref(true);
        const epsiASelected: Ref<boolean> = ref(false);
        const key: Ref<number> = ref(0);

        const context: ComputedRef<string> = computed(() =>
            he.decode((epsiCSelected.value ? epsiCReportData.value : epsiAReportData.value)?.context ?? ''),
        );
        const report: ComputedRef<ITeamReportResponse> = computed(() => (epsiCSelected.value ? epsiCReportData.value : epsiAReportData.value));
        const participantsCompletedInvited: ComputedRef<string> = computed(() =>
            t(`${props.type}.PARTICIPANTS_COMPLETED_VS_INVITED`, {
                invitedParticipants: report.value.invitedParticipants,
                completedParticipants: report.value.completedParticipants,
            }),
        );
        const categoryKeys: string[] = Object.keys(CategoryType).filter((key) => {
            return CategoryType[key] !== CategoryType.General;
        });

        const scores = (data: ITeamReportResponse, category: string): BarChartScore => {
            if (data.dateReport && data.dateReport.sections) {
                const section = data.dateReport.sections.find((sec: ISection) => sec.category == category);
                return new BarChartScore({
                    teamMedian: section.teamMedian,
                    positiveAverage: section.positiveAverage,
                    negativeAverage: section.negativeAverage,
                    tooltipScore: section.teamMedian,
                    participants: section.participants,
                    teamQuartile: section.teamQuartile,
                    standardDeviation: section.standardDeviation,
                });
            }
            return new BarChartScore();
        };

        const getProjectData = async (): Promise<void> => {
            const service = new ReportService();
            const response = await service.getProjectData(projectId, token);
            projectData.value = response;
        };

        const getTeamReports = async (): Promise<void> => {
            const service = new ReportService();
            epsiCReportData.value = await service.getTeamReport(projectId, token, 'epsi_c');
            epsiAReportData.value = await service.getTeamReport(projectId, token, 'epsi_anonymous');
        };

        onMounted(async () => {
            await getProjectData();
            await getTeamReports();

            const lang = store.getters.language;
            lang.section = 'survey';
            const currentReportAlias = lang.reportAlias || '';
            lang.reportAlias = null;

            await store.dispatch('setLanguage', lang);

            lang.reportAlias = currentReportAlias;
            await store.dispatch('setLanguage', lang);
            comparing.value = false;
            key.value++;

            loading.value = false;
        });

        return {
            loading,
            report,
            context,
            CategoryType,
            comparing,
            compareTo,
            projectData,
            participantsCompletedInvited,
            categoryKeys,
            scores,
            thresholdPrefix,
            key,
            epsiCSelected,
            epsiASelected,
            epsiAReportData,
            epsiCReportData,
        };
    },
});
